import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { ROLE_HOME_ROUTES } from "../constants/roles";
import "./protectedRoute.css";

/*
 * ProtectedRoute
 * --------------
 * Wraps a route element. Behaviour:
 *  1. While auth is rehydrating from localStorage → show a small loader.
 *  2. If not signed in → redirect to /signin and remember where they were going
 *     (via location state) so we can send them back after login.
 *  3. If signed in but their role isn't in `allowedRoles` → bounce them to
 *     their own role's home workspace.
 *
 * Usage:
 *   <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGER]}>
 *     <AppLayout />
 *   </ProtectedRoute>
 */
export default function ProtectedRoute({ children, allowedRoles }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Avoid flashing the login page while we're still reading localStorage.
  if (isLoading) {
    return (
      <div className="protected-loading">
        <div className="protected-spinner" aria-hidden="true" />
        <p>Loading…</p>
      </div>
    );
  }

  // Not logged in — send to signin and preserve intended destination.
  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Logged in but wrong role for this route — send to their own home.
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to={ROLE_HOME_ROUTES[user.role] ?? "/signin"} replace />;
  }

  return children;
}
